// The first screen needs only a few line icons (and the clay heart in the
// steps). The collection's sets load on their own (the line set straight
// away, the others when their tab opens) and each hero icon when it is
// shown, so the page starts with a few small files instead of hundreds.
import '../dist/icons/arrow-right.js';
import '../dist/icons/bell.js';
import '../dist/icons/check-circle.js';
import '../dist/icons/close.js';
import '../dist/icons/copy.js';
import '../dist/icons/eye.js';
import '../dist/icons/heart.js';
import '../dist/icons/map-pin.js';
import '../dist/icons/search.js';
import '../dist/icons/settings.js';
import '../dist/icons/star.js';
import '../dist/icons/theme.js';
import '../dist/icons3d/heart.js';
import type { IconClass, InteractiveIcon } from '../dist/core/index.js';
import { initTheme } from './theme.ts';

initTheme();

type SetName = 'line' | 'glass' | 'fluent' | '3d' | 'gl';

/** The page's elements by id (they all exist in index.html). */
const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
/** Creates an icon element by tag. */
const make = (tag: string) => document.createElement(tag) as InteractiveIcon;
const iconClass = (icon: InteractiveIcon) => icon.constructor as IconClass;
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const FEATURED = ['heart', 'map-pin', 'bell', 'star', 'search', 'theme'];

const loaders: Record<SetName, () => Promise<IconClass[]>> = {
  line: async () => (await import('../dist/icons/index.js')).icons,
  glass: async () => (await import('../dist/glass/index.js')).glassIcons,
  fluent: async () => (await import('../dist/fluent/index.js')).fluentIcons,
  '3d': async () => (await import('../dist/icons3d/index.js')).icons3d,
  // Only the WebGL models need three.js (from the CDN).
  gl: async () => (await import('../dist/gl/index.js')).glIcons,
};
const sets: Partial<Record<SetName, Promise<IconClass[]>>> = {};
const loadSet = (name: SetName): Promise<IconClass[]> => (sets[name] ??= loaders[name]());

// For the detail dialog's style chips: the icon from its whole set, which
// is one file (and is then ready for that set's tab too).
const SET_OF = { line: 'line', glass: 'glass', fluent: 'fluent', clay: '3d', model: 'gl' } as const;
const SUFFIX = { line: '', glass: '-glass', fluent: '-fluent', clay: '-3d', model: '-gl' } as const;
const loadStyle = async (variant: keyof typeof SET_OF, name: string): Promise<IconClass | null> => {
  const tag = `ii-${name}${SUFFIX[variant]}`;
  return (await loadSet(SET_OF[variant])).find((Icon) => Icon.tagName === tag) ?? null;
};
let current: SetName = 'line';

const shortTitle = (title: string) => title.replace(/ (WebGL|3D|Glass|Fluent)$/, '');

/* ---------- Copy helpers ---------- */
async function copy(text: string, button: HTMLElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    return;
  }
  if (!button.classList.contains('pill-small')) return;
  button.textContent = 'Copied';
  setTimeout(() => { button.textContent = 'Copy'; }, 1200);
}
$('install').addEventListener('click', (e) => {
  const button = e.currentTarget as HTMLElement;
  copy('npm i @interactive-icons/icons', button);
  button.querySelector('ii-copy')?.restart();
});

/* ---------- Hero stage ---------- */
const stageButton = $('stage-button');
const picker = $('picker');
let featured = 0;
let look = 'glass';
let autoplay = !reduceMotion;
let autoTimer: ReturnType<typeof setTimeout> | undefined;

// Each hero icon on its own, so the first one doesn't wait for a whole set.
// (Literal imports, one per featured icon: a pattern like `${name}.js` would
// split every icon of the set into its own file.)
const heroLoaders: Record<string, Record<string, () => Promise<unknown>>> = {
  glass: {
    'heart': () => import('../dist/glass/heart.js'),
    'map-pin': () => import('../dist/glass/map-pin.js'),
    'bell': () => import('../dist/glass/bell.js'),
    'star': () => import('../dist/glass/star.js'),
    'search': () => import('../dist/glass/search.js'),
    'theme': () => import('../dist/glass/theme.js'),
  },
  fluent: {
    'heart': () => import('../dist/fluent/heart.js'),
    'map-pin': () => import('../dist/fluent/map-pin.js'),
    'bell': () => import('../dist/fluent/bell.js'),
    'star': () => import('../dist/fluent/star.js'),
    'search': () => import('../dist/fluent/search.js'),
    'theme': () => import('../dist/fluent/theme.js'),
  },
};

async function showFeatured(index: number, { play = true } = {}): Promise<void> {
  featured = index;
  const name = FEATURED[index];
  const tag = `ii-${name}-${look}`;
  if (!customElements.get(tag)) {
    await heroLoaders[look]?.[name]?.();
    if (tag !== `ii-${FEATURED[featured]}-${look}`) return; // moved on meanwhile
  }
  const icon = make(tag);
  // The stage button is the trigger target: clicks play it, and the tilt
  // follows the cursor across the whole stage.
  icon.setAttribute('trigger', 'click');
  icon.setAttribute('label', shortTitle((customElements.get(tag) as IconClass | undefined)?.meta.title ?? name));
  stageButton.replaceChildren(icon);
  stageButton.setAttribute('aria-label', `Play the ${name.replace('-', ' ')} icon`);
  for (const [i, chip] of [...picker.children].entries()) chip.setAttribute('aria-selected', String(i === index));
  if (play) requestAnimationFrame(() => (iconClass(icon).toggle ? icon.toggle() : icon.play()));
}

function scheduleNext(): void {
  clearTimeout(autoTimer);
  if (!autoplay) return;
  autoTimer = setTimeout(() => {
    showFeatured((featured + 1) % FEATURED.length);
    scheduleNext();
  }, 3600);
}

function stopAutoplay(): void {
  autoplay = false;
  clearTimeout(autoTimer);
  $('stage-hint').style.opacity = '0.6';
}

FEATURED.forEach((name, i) => {
  const chip = document.createElement('button');
  chip.setAttribute('role', 'tab');
  chip.setAttribute('aria-label', name.replace('-', ' '));
  const mini = document.createElement(`ii-${name}`);
  mini.setAttribute('size', '22');
  mini.setAttribute('trigger', 'manual');
  chip.append(mini);
  chip.addEventListener('click', () => {
    stopAutoplay();
    showFeatured(i);
  });
  picker.append(chip);
});

$('stage').addEventListener('pointerdown', stopAutoplay, { once: true });

for (const button of document.querySelectorAll<HTMLButtonElement>('#stage-looks button')) {
  button.addEventListener('click', () => {
    look = button.dataset.look ?? 'glass';
    $('stage').dataset.look = look;
    for (const b of document.querySelectorAll('#stage-looks button')) b.setAttribute('aria-selected', String(b === button));
    showFeatured(featured);
  });
}

showFeatured(0, { play: !reduceMotion });
scheduleNext();

/* ---------- Collection ---------- */
// Each set's tiles are made once and kept: switching tabs moves them back
// into the grid, and searching only hides the tiles that don't match, so
// typing never rebuilds hundreds of icons.
const grid = $('grid');
interface Tile { el: HTMLElement; text: string }
const tiles: Partial<Record<SetName, Tile[]>> = {};
let rendering = 0;
let shown: SetName | null = null;

function makeTiles(set: SetName, all: IconClass[]): Tile[] {
  return all.map((Icon) => {
    const tile = document.createElement('button');
    tile.className = 'tile';
    tile.dataset.set = set;
    const icon = make(Icon.tagName);
    // Hover plays the motion; clicking the tile opens the detail view instead of toggling.
    icon.setAttribute('trigger', 'hover');
    const label = document.createElement('span');
    label.className = 'tile-name';
    label.textContent = shortTitle(Icon.meta.title);
    tile.append(icon, label);
    tile.setAttribute('aria-label', `${shortTitle(Icon.meta.title)}: details`);
    tile.addEventListener('click', () => { void import('./detail.ts').then(({ openDetail }) => openDetail(Icon, loadStyle)); });
    const { name, title, tags } = Icon.meta;
    return { el: tile, text: [name, title, ...tags].join(' ').toLowerCase() };
  });
}

async function renderGrid(): Promise<void> {
  const ticket = ++rendering;
  const set = current;
  const all = await loadSet(set);
  if (ticket !== rendering) return; // a newer tab or search won
  const list = (tiles[set] ??= makeTiles(set, all));
  if (shown !== set) {
    grid.replaceChildren(...list.map((t) => t.el));
    shown = set;
  }
  const q = $<HTMLInputElement>('q').value.trim().toLowerCase();
  let matches = 0;
  for (const { el, text } of list) {
    const match = !q || text.includes(q);
    if (el.hidden === match) el.hidden = !match;
    if (match) matches++;
  }
  $('empty').hidden = matches > 0;
  const total = list.length;
  const notes: Partial<Record<SetName, string>> = { glass: ', in Liquid Glass', fluent: ', in Fluent 3D', gl: ', modelled and rendered in real time', '3d': ', in clay' };
  $('count').textContent = q ? `${matches} of ${total} icons` : `${total} icons${notes[set] ?? ''}`;
}

for (const tab of document.querySelectorAll<HTMLButtonElement>('#sets button')) {
  const name = (tab.dataset.set ?? 'line') as SetName;
  // Start fetching a set as soon as the pointer heads for its tab.
  tab.addEventListener('pointerenter', () => { loadSet(name).catch(() => {}); }, { once: true });
  tab.addEventListener('click', () => {
    current = name;
    for (const t of document.querySelectorAll('#sets button')) t.setAttribute('aria-selected', String(t === tab));
    renderGrid().catch((error) => {
      // If three.js can't load, the rest of the page is unaffected and the Models tab hides.
      console.warn(`Couldn't load the ${name} icons`, error);
      delete sets[name];
      if (name === 'gl') tab.hidden = true;
      (document.querySelector<HTMLButtonElement>('#sets [data-set="line"]'))?.click();
    });
  });
}
$('q').addEventListener('input', renderGrid);
$('q').addEventListener('focus', () => $<InteractiveIcon>('search-icon').play());
renderGrid();

/* ---------- Performance overlay (?perf) ---------- */
// Shows where frame time goes on this device: open the site with ?perf,
// use it normally, then press "Copy report".
if (new URLSearchParams(location.search).has('perf')) {
  void import('../dist/core/index.js').then(({ frameStats, gpu, pixelRatio, renderScale, getSettings }) => {
    const panel = document.createElement('div');
    panel.style.cssText = 'position:fixed;right:12px;bottom:12px;z-index:99;font:12px/1.45 ui-monospace,monospace;'
      + 'background:rgb(0 0 0/.82);color:#e6e6e6;padding:10px 12px;border-radius:10px;white-space:pre;max-width:min(92vw,420px);overflow:auto';
    const text = document.createElement('div');
    const button = document.createElement('button');
    button.textContent = 'Copy report';
    button.style.cssText = 'margin-top:8px;font:inherit;padding:3px 8px;border-radius:6px;border:0;cursor:pointer';
    panel.append(text, button);
    document.body.append(panel);

    const frames: number[] = [];
    let last = performance.now();
    const tick = (now: number) => {
      frames.push(now - last);
      last = now;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    const env = () => {
      const g = gpu();
      return `${navigator.userAgent}\nGPU: ${g.renderer} (tier ${g.tier}, hardware ${g.hardware})\n`
        + `dpr ${devicePixelRatio} · canvas ratio ${pixelRatio().toFixed(2)} · render scale ${renderScale()} · settings ${JSON.stringify(getSettings())}`;
    };
    const history: string[] = [];
    let before = frameStats();
    setInterval(() => {
      const now = frameStats();
      const n = Math.max(1, now.frames - before.frames);
      const per = (key: 'animate' | 'setup' | 'draw' | 'gpuWait' | 'copy') => ((now[key] - before[key]) / n).toFixed(2).padStart(6);
      const sample = frames.splice(0);
      const worst = Math.max(0, ...sample);
      const slow = sample.filter((f) => f > 20).length;
      const line = `${`${sample.length} fps`.padEnd(8)} worst ${worst.toFixed(0).padStart(4)} ms · ${slow} frames >20 ms\n`
        + `icon loop ${now.frames - before.frames} frames, ms per frame:\n`
        + `  animate ${per('animate')}  setup ${per('setup')}  draw ${per('draw')} (${((now.drawn - before.drawn) / n).toFixed(1)} icons)\n`
        + `  gpu wait ${per('gpuWait')}  copy ${per('copy')}`;
      before = now;
      text.textContent = line;
      history.push(`[${current} tab] ${line.replace(/\n/g, ' | ')}`);
      if (history.length > 30) history.shift();
    }, 1000);
    button.addEventListener('click', () => {
      void navigator.clipboard.writeText(`${env()}\n\n${history.join('\n')}`).then(() => {
        button.textContent = 'Copied';
        setTimeout(() => { button.textContent = 'Copy report'; }, 1200);
      });
    });
  });
}
